import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Search, Users, Heart, Building2, Loader2 } from 'lucide-react';

export default function GlobalSearch({ userRole }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ employees: [], products: [], employers: [], enrollments: [] });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length >= 2) {
      performSearch();
    } else {
      setResults({ employees: [], products: [], employers: [], enrollments: [] });
      setShowResults(false);
    }
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    setShowResults(true);
    
    try {
      const searchLower = query.toLowerCase();
      
      const promises = [];

      // Search employees (admin/employer_hr only)
      if (userRole === 'admin' || userRole === 'sub_admin' || userRole === 'employer_hr') {
        promises.push(
          api.entities.Employee.list().then(data => 
            data.filter(emp => 
              emp.first_name?.toLowerCase().includes(searchLower) ||
              emp.last_name?.toLowerCase().includes(searchLower) ||
              emp.user_email?.toLowerCase().includes(searchLower)
            ).slice(0, 5)
          )
        );
      } else {
        promises.push(Promise.resolve([]));
      }

      // Search products (all users)
      promises.push(
        api.entities.BenefitProduct.list().then(data =>
          data.filter(product =>
            product.name?.toLowerCase().includes(searchLower) ||
            product.short_description?.toLowerCase().includes(searchLower)
          ).slice(0, 5)
        )
      );

      // Search employers (admin only)
      if (userRole === 'admin' || userRole === 'sub_admin') {
        promises.push(
          api.entities.Employer.list().then(data =>
            data.filter(emp =>
              emp.company_name?.toLowerCase().includes(searchLower)
            ).slice(0, 5)
          )
        );
      } else {
        promises.push(Promise.resolve([]));
      }

      const [employeesData, productsData, employersData] = await Promise.all(promises);

      setResults({
        employees: employeesData,
        products: productsData,
        employers: employersData,
        enrollments: []
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalResults = results.employees.length + results.products.length + results.employers.length;

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search everything..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setShowResults(true)}
          className="pl-10 bg-white"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
        )}
      </div>

      {/* Results Dropdown */}
      {showResults && query.length >= 2 && (
        <Card className="absolute top-full mt-2 w-full max-w-2xl z-50 shadow-xl max-h-[500px] overflow-y-auto">
          <div className="p-4">
            {totalResults === 0 && !loading && (
              <div className="text-center py-8">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No results found for "{query}"</p>
              </div>
            )}

            {/* Employees */}
            {results.employees.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-700">Employees</p>
                </div>
                <div className="space-y-1">
                  {results.employees.map(emp => (
                    <Link
                      key={emp.id}
                      to={createPageUrl('EmployeeManagement')}
                      onClick={() => setShowResults(false)}
                      className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <p className="font-medium text-slate-900">
                        {emp.first_name} {emp.last_name}
                      </p>
                      <p className="text-sm text-slate-600">{emp.user_email}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Products */}
            {results.products.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-4 h-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-700">Benefits</p>
                </div>
                <div className="space-y-1">
                  {results.products.map(product => (
                    <Link
                      key={product.id}
                      to={createPageUrl(`BenefitDetail?id=${product.id}`)}
                      onClick={() => setShowResults(false)}
                      className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <p className="font-medium text-slate-900">{product.name}</p>
                      <p className="text-sm text-slate-600">{product.short_description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Employers */}
            {results.employers.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-700">Employers</p>
                </div>
                <div className="space-y-1">
                  {results.employers.map(employer => (
                    <Link
                      key={employer.id}
                      to={createPageUrl('Employers')}
                      onClick={() => setShowResults(false)}
                      className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <p className="font-medium text-slate-900">{employer.company_name}</p>
                      <p className="text-sm text-slate-600">{employer.city}, {employer.state}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}